import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {CreatePennylaneAccountPayload} from "@mytour/shared";
import {FloatingField} from "@mytour/ui/form";
import type {Control, FieldErrors} from "react-hook-form";
import {FormGroup, Input, Label, Spinner} from "reactstrap";
import {TINTED_CARD_STYLE} from "./PennylaneStepStyles.js";

// Le schéma Zod du payload (`CreatePennylaneAccountPayloadSchema`) vit dans
// `@mytour/shared`, pas ici : il est aussi la source de vérité de la
// validation `ZodValidationPipe` côté backend (docs/conventions/backend.md).
// Ne porte pas `email` : le champ est affiché mais non soumis (#1099).
export type PennylaneAccountRecapFormData = CreatePennylaneAccountPayload;

type ServerDefaults = {
  siret: string | null;
  legalName: string;
  phoneNumber: string | null;
};

export function getPennylaneAccountRecapDefaultValues({
  siret,
  legalName,
  phoneNumber,
}: ServerDefaults): PennylaneAccountRecapFormData {
  return {
    siret: siret ?? "",
    legalName,
    phoneNumber: phoneNumber ?? "",
  };
}

/**
 * Statut de diffusion Sirene résolu par l'appelant (`PennylaneRecapModal`) :
 * ce composant affiche seulement, sans hook de query ni debounce
 * (docs/conventions/frontend.md).
 */
export type SiretDiffusionStatus = "partial" | "notfound" | null;

type Props = {
  control: Control<PennylaneAccountRecapFormData>;
  errors: FieldErrors<PennylaneAccountRecapFormData>;
  disabled: boolean;
  siretDiffusionStatus: SiretDiffusionStatus;
  isCheckingSiretDiffusion: boolean;
  /**
   * Email en lecture seule, jamais modifiable (#1099) : irrattrapable en cas
   * d'erreur (#1094). Peut être l'email stocké d'un retry plutôt que celui
   * du manager connecté (#1106/#1114).
   */
  email: string;
};

/**
 * Récap de création du compte Pennylane (#651) — `siret`/`legalName`/
 * `phoneNumber` sont pré-remplis depuis les valeurs serveur mais éditables
 * (décision #771 : la modal récap EST l'étape où l'on complète/corrige).
 * `email` fait exception : affiché en lecture seule, jamais soumis (#1099).
 */
export function PennylaneAccountRecapForm({
  control,
  errors,
  disabled,
  siretDiffusionStatus,
  isCheckingSiretDiffusion,
  email,
}: Props) {
  return (
    <>
      <div style={{position: "relative"}}>
        <FloatingField
          name="siret"
          label="SIRET"
          control={control}
          error={errors.siret}
          disabled={disabled}
          required
        />
        {isCheckingSiretDiffusion && (
          <Spinner
            size="sm"
            color="secondary"
            style={{position: "absolute", right: "0.9rem", top: "0.9rem"}}
          />
        )}
      </div>
      {isCheckingSiretDiffusion && (
        <div className="small text-muted mb-2 d-flex align-items-center gap-2">
          <Spinner size="sm" color="secondary" style={{width: "0.7rem", height: "0.7rem"}} />
          <span>Vérification…</span>
        </div>
      )}
      {siretDiffusionStatus && <SiretDiffusionAlert status={siretDiffusionStatus} />}
      <FloatingField
        name="legalName"
        label="Raison sociale"
        control={control}
        error={errors.legalName}
        disabled={disabled}
        required
      />
      <FormGroup floating>
        <Input
          id="field-email"
          type="email"
          value={email}
          disabled
          readOnly
          placeholder="Email qui recevra l'invitation Pennylane"
        />
        <Label for="field-email">Email qui recevra l'invitation Pennylane</Label>
      </FormGroup>
      <FloatingField
        name="phoneNumber"
        label="Téléphone mobile"
        control={control}
        error={errors.phoneNumber}
        disabled={disabled}
        required
      />
      <details className="mt-1">
        <summary className="small text-primary" style={{cursor: "pointer"}}>
          Quelles données Pennylane va recevoir, et pourquoi ?
        </summary>
        <ul className="text-muted small mt-2 mb-0 ps-3">
          <li>
            Pour créer votre compte utilisateur : vos nom, prénom, email et téléphone. Si une
            demande précédente a déjà réservé cet utilisateur, il est repris tel quel et rien n'est
            renvoyé (téléphone compris).
          </li>
          <li>
            Pour déclarer votre entreprise : raison sociale, adresse administrative, code APE et
            numéro SIREN (les 9 premiers chiffres du SIRET saisi).
          </li>
          <li>
            Après activation du compte et signature du mandat, l'inscription de votre entreprise à
            l'annuaire officiel de la facturation électronique (consultable par tous) est demandée
            ; l'annuaire peut la refuser.
          </li>
          <li>
            Une fois le compte créé, corriger le SIRET ou la raison sociale met aussi à jour votre
            fiche entreprise MyTour et les documents qui en reprennent les informations.
          </li>
        </ul>
      </details>
    </>
  );
}

/**
 * Encart avant création (#1100, mock-up V10) : ne porte que le fait métier et
 * la ligne horaire, seule info actionnable à ce stade. Le détail du canal de
 * déblocage (chat Pennylane) vit sur l'étape de confirmation post-Confirmer.
 */
const SIRET_DIFFUSION_ALERT_COPY: Record<Exclude<SiretDiffusionStatus, null>, string> = {
  partial:
    "Votre entreprise est en diffusion restreinte au répertoire Sirene : une vérification d'identité supplémentaire sera nécessaire. Nous vous conseillons de faire cette démarche du lundi au vendredi, de 8h30 à 18h30.",
  notfound:
    "Nous n'avons pas trouvé d'entreprise pour ce SIRET. Vérifiez la saisie, ou continuez si elle est récente ou non diffusée. Si elle est non diffusée, nous vous conseillons de faire cette démarche du lundi au vendredi, de 8h30 à 18h30.",
};

/**
 * Encart informatif seul (jamais bloquant, cf. `SIRET_DIFFUSION_ALERT_COPY`) —
 * même traitement violet DS pour les deux statuts (mock-up validé #1042).
 */
function SiretDiffusionAlert({status}: {status: Exclude<SiretDiffusionStatus, null>}) {
  return (
    <div
      className="mb-3"
      style={{
        ...TINTED_CARD_STYLE,
        display: "flex",
        gap: "9px",
        alignItems: "flex-start",
        fontSize: "0.84rem",
        color: "#3730a3",
      }}
    >
      <FontAwesomeIcon
        icon="circle-info"
        style={{marginTop: "2px", color: "var(--mt-color-action)"}}
      />
      <span>{SIRET_DIFFUSION_ALERT_COPY[status]}</span>
    </div>
  );
}
